


// SummaryReportModal.js
import React, { useState, useEffect } from 'react';
import {
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 FormControl,
 InputLabel,
 Select,
 MenuItem,
 OutlinedInput,
 Chip,
 Box,
 Stack,
 Button,
 FormHelperText,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
 PaperProps: {
  style: {
   maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
   width: 250,
  },
 },
};

export default function SummaryReportModal({
 open,
 onClose,
 onGenerate,
 loading = false,
 brandName,
 categories = [],
 llms = [],
 markets = [],
 persona = [],
}) {
 const [selectedCategory, setSelectedCategory] = useState('');
 const [selectedLLMs, setSelectedLLMs] = useState([]);
 const [selectedMarkets, setSelectedMarkets] = useState([]);
 const [selectedPersona, setSelectedPersona] = useState([]);
 const [errors, setErrors] = useState({});

 // Initialize with default values if only one option exists
 useEffect(() => {
  if (open) {
   setSelectedCategory(categories.length === 1 ? categories[0] : '');
   setSelectedLLMs(llms.length === 1 ? llms : []);
   setSelectedMarkets(markets.length === 1 ? markets : []);
   setSelectedPersona(persona.length === 1 ? persona : []);
   setErrors({});
  }
 }, [open, categories, llms, markets, persona]);

 const handleCategoryChange = (e) => {
  setSelectedCategory(e.target.value);
  if (errors.category) {
   setErrors((prev) => ({ ...prev, category: false }));
  }
 };

 const handleLLMChange = (e) => {
  const { value } = e.target;
  setSelectedLLMs(typeof value === 'string' ? value.split(',') : value);
  if (errors.llms) {
   setErrors((prev) => ({ ...prev, llms: false }));
  }
 };

 const handleMarketChange = (e) => {
  const { value } = e.target;
  setSelectedMarkets(typeof value === 'string' ? value.split(',') : value);
  if (errors.markets) {
   setErrors((prev) => ({ ...prev, markets: false }));
  }
 };

 const handlePersonaChange = (e) => {
  const { value } = e.target;
  setSelectedPersona(typeof value === 'string' ? value.split(',') : value);
  if (errors.persona) {
   setErrors((prev) => ({ ...prev, persona: false }));
  }
 };

 const validateForm = () => {
  const newErrors = {};

  if (!selectedCategory) {
   newErrors.category = true;
  }
  if (selectedLLMs.length === 0) {
   newErrors.llms = true;
  }
  if (selectedMarkets.length === 0) {
   newErrors.markets = true;
  }

  if (selectedPersona.length === 0) {
   newErrors.persona = true;
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
 };

 const handleGenerate = () => {
  if (validateForm()) {
   onGenerate({
    category: selectedCategory,
    llms: selectedLLMs,
    markets: selectedMarkets,
    persona: selectedPersona,
   });
  }
 };

 const handleRemoveLLM = (llm) => {
  setSelectedLLMs((prev) => prev.filter((item) => item !== llm));
 };

 const handleRemoveMarket = (market) => {
  setSelectedMarkets((prev) => prev.filter((item) => item !== market));
 };

 const handleRemovePersona = (persona) => {
  setSelectedPersona((prev) => prev.filter((item) => item !== persona));
 };

 return (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
   <DialogTitle>
    Generate Summary Report {brandName && `- ${brandName}`}
   </DialogTitle>
   <DialogContent>
    <Stack spacing={3} sx={{ mt: 2 }}>
     {/* Category Selection - Single Select */}
     <FormControl fullWidth error={errors.category}>
      <InputLabel>Category *</InputLabel>
      <Select
       value={selectedCategory}
       onChange={handleCategoryChange}
       label="Category *"
      >
       <MenuItem value="">
        <em>Select a category</em>
       </MenuItem>
       {categories.map((category) => (
        <MenuItem key={category} value={category}>
         {category}
        </MenuItem>
       ))}
      </Select>
      {errors.category && (
       <FormHelperText>Please select a category</FormHelperText>
      )}
     </FormControl>

     {/* LLM Selection - Multi-Select Dropdown */}
     <FormControl fullWidth error={errors.llms}>
      <InputLabel>LLMs *</InputLabel>
      <Select
       multiple
       value={selectedLLMs}
       onChange={handleLLMChange}
       input={<OutlinedInput label="LLMs *" />}
       renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
         {selected.map((value) => (
          <Chip
           key={value}
           label={value}
           onDelete={() => handleRemoveLLM(value)}
           onMouseDown={(e) => e.stopPropagation()}
          />
         ))}
        </Box>
       )}
       MenuProps={MenuProps}
      >
       {llms.map((llm) => (
        <MenuItem key={llm} value={llm}>
         {llm}
        </MenuItem>
       ))}
      </Select>
      {errors.llms && (
       <FormHelperText>Please select at least one LLM</FormHelperText>
      )}
     </FormControl>

     {/* Market Selection - Multi-Select Dropdown */}
     <FormControl fullWidth error={errors.markets}>
      <InputLabel>Markets *</InputLabel>
      <Select
       multiple
       value={selectedMarkets}
       onChange={handleMarketChange}
       input={<OutlinedInput label="Markets *" />}
       renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
         {selected.map((value) => (
          <Chip
           key={value}
           label={value}
           onDelete={() => handleRemoveMarket(value)}
           onMouseDown={(e) => e.stopPropagation()}
          />
         ))}
        </Box>
       )}
       MenuProps={MenuProps}
      >
       {markets.map((market) => (
        <MenuItem key={market} value={market}>
         {market}
        </MenuItem>
       ))}
      </Select>
      {errors.markets && (
       <FormHelperText>Please select at least one market</FormHelperText>
      )}
     </FormControl>

     {/* Persona Selection - Multi-Select Dropdown */}
     <FormControl fullWidth error={errors.persona}>
      <InputLabel>Persona *</InputLabel>
      <Select
       multiple
       value={selectedPersona}
       onChange={handlePersonaChange}
       input={<OutlinedInput label="Persona *" />}
       renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
         {selected.map((value) => (
          <Chip
           key={value}
           label={value}
           onDelete={() => handleRemovePersona(value)}
           onMouseDown={(e) => e.stopPropagation()}
          />
         ))}
        </Box>
       )}
       MenuProps={MenuProps}
      >
       {persona.map((p) => (
        <MenuItem key={p} value={p}>
         {p}
        </MenuItem>
       ))}
      </Select>
      {errors.persona && (
       <FormHelperText>Please select at least one persona</FormHelperText>
      )}
     </FormControl>
    </Stack>
   </DialogContent>
   <DialogActions sx={{ p: 2 }}>
    <Button onClick={onClose} disabled={loading}>
     Cancel
    </Button>
    <Button
     onClick={handleGenerate}
     variant="contained"
     color="primary"
     disabled={loading}
    >
     Generate
    </Button>
   </DialogActions>
  </Dialog>
 );
}
